import React from 'react';

const Content = () => (
  <>
          <section className="privacy-section">
            <h2>1) Controller and scope</h2>
            <p>
              The controller of your personal data processed in connection with the OW Owner
              mobile application (the "App") is Diddyinnovation Limited ("we", "us", "our").
            </p>
            <p>
              This Privacy Policy covers data processed when you create and use an App account,
              use App features (including location-based features), contact us via in-app forms,
              and use payment-related features (where enabled).
            </p>
            <div className="contact-info">
              <p><strong>Company:</strong> Diddyinnovation Limited</p>
              <p><strong>Address:</strong> Coral Bay 91, 8575 Pegeia, Cyprus</p>
              <p><strong>Market:</strong> Cyprus</p>
              <p><strong>Hosting/processing infrastructure:</strong> AWS (Frankfurt, EU)</p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>2) How to contact us</h2>
            <p>
              You may contact us regarding privacy matters and exercise your GDPR rights using the
              "Contact us" form available in the App profile section. Please describe your request
              in as much detail as possible so we can verify it and respond efficiently.
            </p>
          </section>

          <section className="privacy-section">
            <h2>3) Personal data we process</h2>
            <p>Depending on how you use the App and which features are enabled, we may process:</p>

            <h3>A. Account and profile data</h3>
            <ul>
              <li>Email address</li>
              <li>Profile fields you provide in the App (e.g., name and company-related fields)</li>
              <li>Account identifiers and authentication data (e.g., login tokens)</li>
            </ul>

            <h3>B. Operational/service data</h3>
            <ul>
              <li>Information necessary to provide App features (e.g., identifiers, timestamps, status information)</li>
            </ul>

            <h3>C. Payment-related data (where payment features are enabled)</h3>
            <ul>
              <li>Payment method selection (e.g., Apple Pay, Google Pay, card, bank transfer)</li>
              <li>Payment status and technical identifiers received from payment providers</li>
              <li>We do not intend to store full card details in our systems (card and wallet payments are processed by specialized payment providers)</li>
            </ul>

            <h3>D. Location data (stored on the backend)</h3>
            <ul>
              <li>Device location (precise or approximate, depending on device settings)</li>
              <li>We store location data on our backend systems hosted in AWS Frankfurt (EU) when you use location-based features and have granted location permissions</li>
            </ul>

            <h3>E. Device and technical data</h3>
            <ul>
              <li>Device and app identifiers, app version, language, OS version</li>
              <li>Diagnostic/security logs necessary to maintain security and reliability</li>
            </ul>

            <h3>F. Communications</h3>
            <ul>
              <li>Information you provide in contact forms and support communications (and our replies)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4) Location data - what we collect, when, and why</h2>
            <p>
              <strong>When location is collected:</strong> We collect location data only when you
              enable location permissions and when the App is used in a way that requires
              location-based functionality (e.g., map-based features or other features that
              depend on location).
            </p>
            <p>
              <strong>Where location is stored:</strong> Location data is stored on our backend
              infrastructure (AWS Frankfurt, EU) and linked to your account and/or session
              identifiers as needed to provide the feature and ensure service continuity across
              devices.
            </p>
            <p><strong>Purposes:</strong></p>
            <ul>
              <li>Enabling location-based App features</li>
              <li>Improving reliability of those features and preventing abuse (e.g., anomalous activity detection)</li>
            </ul>
            <p>
              <strong>Control by the user:</strong> You can disable location permissions at any
              time in your device settings. Disabling location may limit or disable
              location-based features.
            </p>
          </section>

          <section className="privacy-section">
            <h2>5) Purposes of processing and legal bases (GDPR Art. 6)</h2>
            <p>We process personal data for the following purposes:</p>

            <h3>A. Account creation, authentication, profile management, and App operation</h3>
            <p>Legal basis: performance of a contract / taking steps at your request (Art. 6(1)(b) GDPR).</p>

            <h3>B. Providing App features (including location-based features when enabled)</h3>
            <p>
              Legal basis: performance of a contract (Art. 6(1)(b)). Where applicable under
              ePrivacy rules (permissions and similar access to device capabilities), we rely on
              your permission choice and, where required, consent (Art. 6(1)(a)).
            </p>

            <h3>C. Handling payments and related reconciliations (where enabled)</h3>
            <p>
              Legal basis: performance of a contract (Art. 6(1)(b)) and, where relevant,
              compliance with legal obligations (Art. 6(1)(c)). Payment processing itself is
              carried out by payment providers/banks under their own terms and privacy notices.
            </p>

            <h3>D. Customer support and handling requests via the in-app contact form</h3>
            <p>
              Legal basis: legitimate interests (Art. 6(1)(f)) in responding to users and ensuring
              service quality and/or contract (Art. 6(1)(b)).
            </p>

            <h3>E. Security, fraud prevention, and ensuring service stability</h3>
            <p>Legal basis: legitimate interests (Art. 6(1)(f)).</p>

            <h3>F. Notifications (planned feature)</h3>
            <p>
              We may introduce push notifications (e.g., service updates). Legal basis depends on
              the notification type. Service/transactional notifications are typically processed
              under contract/legitimate interests (Art. 6(1)(b) / 6(1)(f)). Marketing
              notifications (if introduced) typically require consent (Art. 6(1)(a)) where
              required by law.
            </p>

            <h3>G. Analytics/crash reporting (planned feature)</h3>
            <p>
              If introduced, the legal basis will depend on the tool and configuration (consent
              (Art. 6(1)(a)) and/or legitimate interests (Art. 6(1)(f)) in line with applicable
              ePrivacy rules). We will update this Privacy Policy when specific tools are enabled.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6) Recipients of data</h2>
            <p>We may share personal data only to the extent necessary with:</p>
            <ul>
              <li>IT infrastructure and hosting providers (AWS infrastructure located in Frankfurt, EU)</li>
              <li>Payment providers/banks (for processing Apple Pay/Google Pay/card/bank transfer transactions, where enabled)</li>
              <li>Vendors providing support/maintenance services under appropriate confidentiality and data processing terms</li>
              <li>Competent public authorities and courts, where legally required</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>7) International transfers</h2>
            <p>
              We currently use AWS infrastructure in Frankfurt (EU). If, in the future, we engage
              providers that process data outside the EEA, we will ensure appropriate safeguards
              under GDPR Chapter V (e.g., Standard Contractual Clauses) and update this Privacy
              Policy accordingly.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8) Retention</h2>
            <p>We retain data only for as long as necessary for the purposes described in this Privacy Policy:</p>
            <ul>
              <li>Account and profile data (email, name/company fields): for the duration of your account and as needed afterwards to resolve disputes, enforce terms, and comply with legal obligations</li>
              <li>Location data stored on the backend: retained for as long as necessary to provide the location-based feature and maintain service continuity; we may also retain limited location-related logs for security and abuse prevention for a limited period</li>
              <li>Contact form communications: for as long as necessary to handle the matter and for evidence/defense purposes</li>
              <li>Security and diagnostic logs: typically for a limited period appropriate to security and troubleshooting</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>9) Account deletion</h2>
            <p>
              The App allows account deletion from the user interface. When you delete your
              account, we will delete or anonymize personal data associated with your account
              without undue delay, except where we must retain certain data to comply with legal
              obligations (e.g., accounting/tax) or to establish, exercise, or defend legal
              claims.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10) Your rights under GDPR/RODO</h2>
            <p>Subject to GDPR conditions and limitations, you have the right to:</p>
            <ul>
              <li>Access your data</li>
              <li>Rectify inaccurate data</li>
              <li>Erase data</li>
              <li>Restrict processing</li>
              <li>Data portability</li>
              <li>Object to processing based on legitimate interests</li>
              <li>Withdraw consent at any time (where processing is based on consent), without affecting the lawfulness of processing before withdrawal</li>
            </ul>
            <p>To exercise your rights, submit a request through the in-app "Contact us" form.</p>
          </section>

          <section className="privacy-section">
            <h2>11) Right to lodge a complaint</h2>
            <p>
              You have the right to lodge a complaint with a supervisory authority. As the
              Controller is established in Cyprus, the competent authority is generally the
              Office of the Commissioner for Personal Data Protection (Cyprus). You may also be
              entitled to lodge a complaint with another supervisory authority in accordance with
              GDPR rules.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12) Automated decision-making</h2>
            <p>
              We do not intend to carry out decisions based solely on automated processing,
              including profiling, that produce legal effects concerning you or similarly
              significantly affect you. If this changes (e.g., marketing profiling), we will
              provide appropriate information and, where required, request consent.
            </p>
          </section>

          <section className="privacy-section">
            <h2>13) Security</h2>
            <p>
              We apply appropriate technical and organizational measures to protect personal
              data, including access control, least-privilege principles, and encryption in
              transit where applicable. Despite these measures, no system can guarantee absolute
              security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>14) Changes to this Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time, including when we introduce
              notifications, analytics, or new features. The current version will be available in
              the App. Where required by law, we will provide additional notice and/or request
              your consent.
            </p>
          </section>

          <section className="privacy-section">
            <h2>15) Contact</h2>
            <div className="contact-info">
              <p><strong>Diddyinnovation Limited</strong></p>
              <p>Coral Bay 91</p>
              <p>8575 Pegeia</p>
              <p>Cyprus</p>
              <p><strong>Contact:</strong> Profile → Contact (in-app contact form)</p>
              <p>For general inquiries, visit our <a href="/contact">Contact Page</a></p>
            </div>
            <p className="highlight-note">
              Note: Apple Pay, Google Pay, card payments and bank transfers may involve
              independent processing of personal data by payment providers and banks under their
              own privacy notices.
            </p>
          </section>
  </>
);

export default Content;
